import { Button, Drawer, Space, Tooltip } from 'antd';
import { memo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '../../Core/Icon';
import { getFilegrantViewerBaseUrl } from '../../../lib/filegrantViewerChoice';
import { getOfficePath } from '../../../lib/officeRoutePath';
import Office from '../../../routes/Office/Office';
import FilegrantViewerHost from '../../../routes/Viewer/FilegrantViewerHost';
import Viewer from '../../../routes/Viewer/Viewer';
import {
  RIGHT_PANEL_WIDTH_PX,
  VIEWER_DRAWER_HEADER_OFFSET_PX,
  VIEWER_DRAWER_MAX_SIZE_VH,
  VIEWER_DRAWER_MIN_SIZE_VH,
  VIEWER_DRAWER_Z_INDEX,
} from '../../../store/viewerDrawerSlice';
import { useAppStore } from '../../../store/store';

/**
 * Nested Details/AI panel. Separate component so FilegrantViewerDrawer body (Host/Viewer)
 * does not re-render on every `rightPanelContent` change (Monaco remount risk).
 */
function ViewerDrawerSidePanel() {
  const { t } = useTranslation();
  const isRightPanelOpen = useAppStore((s) => s.isRightPanelOpen);
  const rightPanelContent = useAppStore((s) => s.rightPanelContent);
  const hasRightPanelCloseButton = useAppStore((s) => s.hasRightPanelCloseButton);
  const closeRightPanel = useAppStore((s) => s.closeRightPanel);

  if (!isRightPanelOpen) return null;

  return (
    <aside
      data-testid="viewer-drawer-side-panel"
      aria-label={t('a11y.detailsPanel')}
      className="border-gray-medium relative flex h-full shrink-0 flex-col overflow-hidden border-l bg-white"
      style={{ width: RIGHT_PANEL_WIDTH_PX, maxWidth: '100%' }}
    >
      {hasRightPanelCloseButton ? (
        <div className="absolute top-1 right-1 z-40 flex shrink-0 items-center">
          <Button
            type="text"
            aria-label={t('a11y.closePanel')}
            onClick={closeRightPanel}
            icon={<Icon name="XIcon" className="h-4 w-4" />}
          />
        </div>
      ) : null}
      <div
        className={`relative h-full min-h-0 overflow-auto px-2 ${hasRightPanelCloseButton ? 'pt-8' : ''}`}
      >
        {rightPanelContent}
      </div>
    </aside>
  );
}

/** Viewer/Office host — memoized so panel open/close on parent does not re-render Monaco. */
const ViewerDrawerHost = memo(function ViewerDrawerHost({
  onRequestClose,
}: {
  onRequestClose: () => void;
}) {
  const kind = useAppStore((s) => s.viewerDrawerKind);
  const fileId = useAppStore((s) => s.viewerDrawerFileId);
  const fileType = useAppStore((s) => s.viewerDrawerFileType);
  const version = useAppStore((s) => s.viewerDrawerVersion);
  const publicFileToken = useAppStore((s) => s.viewerDrawerPublicFileToken);
  const acToken = useAppStore((s) => s.viewerDrawerAcToken);
  const isReadOnly = useAppStore((s) => s.viewerDrawerIsReadOnly);

  const viewerBaseUrl = getFilegrantViewerBaseUrl();
  const useGuestViewer = Boolean(viewerBaseUrl);
  const isOfficeKind = kind === 'office';
  const isViewerKind = kind === 'viewer';

  return (
    <div className="relative h-full min-h-0 min-w-0 flex-1 overflow-hidden">
      {isOfficeKind && fileId && fileType != null ? (
        <Office type={fileType} fileId={fileId} fillParent onRequestClose={onRequestClose} />
      ) : null}
      {isViewerKind && fileId && useGuestViewer ? (
        <FilegrantViewerHost
          viewerBaseUrl={viewerBaseUrl}
          fileId={fileId}
          version={version}
          publicFileToken={publicFileToken}
          acToken={acToken}
          isReadOnly={isReadOnly}
          fillParent
          onRequestClose={onRequestClose}
        />
      ) : null}
      {isViewerKind && fileId && !useGuestViewer ? (
        <Viewer fileId={fileId} fillParent onRequestClose={onRequestClose} />
      ) : null}
    </div>
  );
});

/**
 * Bottom sheet (~95vh default, resizable up to 100vh) hosting ViewerGrant iframe,
 * classic Viewer (`fillParent`), or Office editor. Full-route paths stay via “Open in a new tab”.
 * Details / AI summary / chat render as a nested right split inside the drawer (not global RightPanel),
 * so toggling the panel never shrinks wrapper width or remounts Host/Viewer.
 */
export default function FilegrantViewerDrawer() {
  const { t } = useTranslation();
  const isOpen = useAppStore((s) => s.isViewerDrawerOpen);
  const kind = useAppStore((s) => s.viewerDrawerKind);
  const fileId = useAppStore((s) => s.viewerDrawerFileId);
  const fileType = useAppStore((s) => s.viewerDrawerFileType);
  const version = useAppStore((s) => s.viewerDrawerVersion);
  const publicFileToken = useAppStore((s) => s.viewerDrawerPublicFileToken);
  const acToken = useAppStore((s) => s.viewerDrawerAcToken);
  const isReadOnly = useAppStore((s) => s.viewerDrawerIsReadOnly);
  const sizeVh = useAppStore((s) => s.viewerDrawerSizeVh);
  // Only boolean — content lives in ViewerDrawerSidePanel to avoid Host re-render on content churn.
  const isRightPanelOpen = useAppStore((s) => s.isRightPanelOpen);
  const closeRightPanel = useAppStore((s) => s.closeRightPanel);
  const closeViewerDrawer = useAppStore((s) => s.closeViewerDrawer);
  const setViewerDrawerSizeVh = useAppStore((s) => s.setViewerDrawerSizeVh);

  const isOfficeKind = kind === 'office';
  const isViewerKind = kind === 'viewer';
  const drawerOpen =
    isOpen && Boolean(fileId) && (isViewerKind || (isOfficeKind && fileType != null));

  const drawerSize = `${sizeVh}vh`;
  const maxSizePx =
    typeof window !== 'undefined'
      ? Math.round((window.innerHeight * VIEWER_DRAWER_MAX_SIZE_VH) / 100)
      : undefined;

  const handleResize = (newSizePx: number) => {
    const vh = (newSizePx / window.innerHeight) * 100;
    // Floor here too: rc Drawer only enforces maxSize (min is 0).
    if (vh < VIEWER_DRAWER_MIN_SIZE_VH) {
      setViewerDrawerSizeVh(VIEWER_DRAWER_MIN_SIZE_VH);
      return;
    }
    setViewerDrawerSizeVh(vh);
  };

  const handleCloseDrawer = useCallback(() => {
    closeRightPanel();
    closeViewerDrawer();
  }, [closeRightPanel, closeViewerDrawer]);

  const handleOpenFull = () => {
    if (!fileId) return;
    if (isOfficeKind && fileType != null) {
      handleCloseDrawer();
      window.open(getOfficePath(fileId, fileType), '_blank', 'noopener,noreferrer')?.focus();
      return;
    }
    const url = new URL(`/viewer/${fileId}`, window.location.origin);
    if (version) url.searchParams.set('version', version);
    if (publicFileToken) url.searchParams.set('publicFileToken', publicFileToken);
    if (acToken) url.searchParams.set('acToken', acToken);
    if (isReadOnly) url.searchParams.set('r', '1');
    handleCloseDrawer();
    window.open(url.pathname + url.search, '_blank', 'noopener,noreferrer')?.focus();
  };

  const title = isOfficeKind
    ? t('viewer.editorDrawerTitle', { defaultValue: 'Editor' })
    : t('viewer.previewDrawerTitle', { defaultValue: 'Preview' });

  const openFullLabel = t('viewer.openInNewTab', { defaultValue: 'Open in a new tab' });

  return (
    <Drawer
      title={title}
      placement="bottom"
      size={drawerSize}
      maxSize={maxSizePx}
      resizable={{ onResize: handleResize }}
      open={drawerOpen}
      onClose={handleCloseDrawer}
      closable={false}
      destroyOnClose
      zIndex={VIEWER_DRAWER_Z_INDEX}
      data-testid="filegrant-viewer-drawer"
      styles={{
        wrapper: { width: '100%' },
        body: {
          display: 'flex',
          flexDirection: 'column',
          // Panel height tracks size; subtract header so fillParent host gets a real flex box.
          height: `calc(${drawerSize} - ${VIEWER_DRAWER_HEADER_OFFSET_PX}px)`,
          minHeight: 0,
          overflow: 'hidden',
          // Full-bleed body: chrome inset lives inside viewer UI, not drawer padding.
          padding: 0,
          position: 'relative',
        },
      }}
      extra={
        <Space>
          <Tooltip title={openFullLabel}>
            <Button
              type="text"
              size="small"
              aria-label={openFullLabel}
              onClick={handleOpenFull}
              icon={<Icon name="ExternalLinkIcon" className="h-4 w-4" />}
            >
              <span className="hidden sm:inline">{openFullLabel}</span>
            </Button>
          </Tooltip>
          <Button
            type="text"
            size="small"
            aria-label={t('common.close')}
            onClick={handleCloseDrawer}
            icon={<Icon name="XIcon" className="h-4 w-4" />}
          />
        </Space>
      }
    >
      <div className="flex h-full min-h-0 w-full flex-row">
        {/* Host stays mounted when side panel toggles — only sibling aside mounts. */}
        <ViewerDrawerHost onRequestClose={handleCloseDrawer} />
        {/* Keep mount point stable: boolean only gates child; Host never sees content updates. */}
        {isRightPanelOpen ? <ViewerDrawerSidePanel /> : null}
      </div>
    </Drawer>
  );
}
